/* Native Shopify cart. Product POST forms (including Buy Now) remain native. */
(() => {
  if (window.bgNativeCartInitialized) return;
  window.bgNativeCartInitialized = true;
  const dialog = document.getElementById('bg-cart-dialog');
  if (!dialog) return;
  const cartURL = dialog.dataset.cartUrl;
  let busy = false, opener = null, savedOverflow = '';
  const roots = () => [...document.querySelectorAll('[data-bgc-section]')];
  // JSON-template sections have dynamic IDs; static drawer sections use their filename.
  const sectionId = root => root.closest('[id^="shopify-section-"]')?.id.slice('shopify-section-'.length) || root.dataset.bgcSection;
  function renderingURL() {
    // Keep the cart's active template (?view=...) and locale when refreshing its dynamic ID.
    const url = new URL(document.querySelector('.bgc-shell[data-bgc-section]') ? location.href : cartURL, location.origin);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    url.hash = '';
    return url;
  }
  const message = (selector, text) => roots().forEach(root => {
    const node = root.querySelector(selector);
    if (node) { node.textContent = text; node.hidden = !text; }
  });
  function enhance() {
    document.querySelectorAll('[data-bgc-step]').forEach(button => { button.hidden = false; });
    const count = roots()[0]?.dataset.bgcCount;
    if (count !== undefined) {
      document.querySelectorAll('.Header__CartCount').forEach(node => { node.textContent = count; });
      document.querySelectorAll('.bg-cart-link').forEach(node => { node.setAttribute('aria-label', 'Cart, ' + count + ' items'); });
      document.querySelectorAll('.Header__CartDot').forEach(node => { node.classList.toggle('is-visible', Number(count) > 0); });
    }
  }
  function lock(value) {
    busy = value;
    roots().forEach(root => {
      root.setAttribute('aria-busy', String(value));
      root.querySelectorAll('input, textarea, button:not([data-bgc-close])').forEach(node => {
        if (value && !node.disabled) { node.dataset.bgcLocked = 'true'; node.disabled = true; }
        if (!value && node.dataset.bgcLocked) { node.disabled = false; delete node.dataset.bgcLocked; }
      });
    });
  }
  function close() { dialog.close(); }
  dialog.addEventListener('close', () => {
    document.documentElement.style.overflow = savedOverflow;
    if (opener?.isConnected) opener.focus();
  });
  dialog.addEventListener('click', event => { if (event.target === dialog) close(); });
  async function json(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      // Do not send Accept: application/json to /cart?sections=...: Shopify can
      // negotiate the cart JSON representation instead of returning section HTML.
      // Mutation endpoints already end in .js and return JSON without that header.
      const response = await fetch(url, {credentials: 'same-origin', ...options, signal: controller.signal});
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data?.description === 'string' ? data.description : 'The cart could not be updated. Please check its latest contents.');
      return data;
    } finally { clearTimeout(timeout); }
  }
  async function refresh(focus, sections) {
    const oldRoots = roots();
    if (sections === undefined) {
      const url = renderingURL();
      url.searchParams.set('sections', [...new Set(oldRoots.map(sectionId))].join(','));
      sections = await json(url.href, {cache: 'no-store'});
    }
    // Validate every fragment before changing the page: no half-updated totals or line keys.
    const replacements = oldRoots.map(root => {
      const id = sectionId(root);
      const html = sections?.[id];
      if (typeof html !== 'string') throw new Error('Cart display could not refresh. Reloading the cart…');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = [...parsed.querySelectorAll('[data-bgc-section]')].find(node => node.dataset.bgcSection === id);
      if (!next) throw new Error('Cart display could not refresh. Reloading the cart…');
      return [root, next];
    });
    const {focusId, focusKey, focusStep, activeRoot} = focus;
    replacements.forEach(([old, next]) => {
      // Preserve a draft order note while quantities refresh; checkout submits it natively.
      const note = old.querySelector('[name="note"]');
      const nextNote = next.querySelector('[name="note"]');
      if (note && nextNote && note.dataset.bgcDirty) { nextNote.value = note.value; nextNote.dataset.bgcDirty = 'true'; }
      old.replaceWith(next);
    });
    enhance();
    const scope = roots().find(root => root.dataset.bgcSection === activeRoot);
    let target = focusId ? document.getElementById(focusId) : null;
    if (!target && scope && focusKey) {
      const line = [...scope.querySelectorAll('[data-bgc-key]')].find(node => node.dataset.bgcKey === focusKey);
      target = focusStep ? [...(line?.querySelectorAll('[data-bgc-step]') || [])].find(node => node.dataset.bgcStep === focusStep) : line?.querySelector('[data-bgc-quantity]');
    }
    if (!target && dialog.open && activeRoot) target = dialog.querySelector('[data-bgc-close]');
    target?.focus({preventScroll: true});
  }
  function failed(error) {
    message('[data-bgc-error]', error.message || 'Unable to refresh the cart. Reloading it to check its latest contents…');
    roots().forEach(root => {
      root.querySelector('[data-bgc-recovery]')?.removeAttribute('hidden');
    });
    // A rendering/network error can happen AFTER Shopify accepts the mutation.
    // Never repeat that POST: reconcile with a normal, server-rendered cart page.
    location.assign(renderingURL().href);
  }
  async function perform(url, payload) {
    if (busy) return;
    const active = document.activeElement;
    const focus = {focusId: active?.id, focusKey: active?.closest('[data-bgc-key]')?.dataset.bgcKey, focusStep: active?.dataset.bgcStep, activeRoot: active?.closest('[data-bgc-section]')?.dataset.bgcSection};
    lock(true);
    message('[data-bgc-error]', '');
    message('[data-bgc-status]', 'Updating cart…');
    let failure;
    try {
      let sections;
      if (url) {
        const context = renderingURL();
        const result = await json(url + '.js', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({
          ...payload,
          sections: [...new Set(roots().map(sectionId))],
          sections_url: context.pathname + context.search
        })});
        // Shopify bundles rendered sections into the mutation response (null means rendering failed).
        sections = result.sections ?? null;
      }
      await refresh(focus, sections);
    } catch (error) { failure = error; }
    finally {
      lock(false);
      message('[data-bgc-status]', '');
    }
    if (failure) failed(failure);
    else message('[data-bgc-status]', 'Cart updated.');
  }
  async function open(trigger) {
    if (dialog.dataset.bgcEnabled !== 'true' || typeof dialog.showModal !== 'function') { location.assign(cartURL); return; }
    if (!dialog.open) {
      opener = trigger || document.activeElement;
      savedOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
    await perform();
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const closeButton = event.target.closest('[data-bgc-close]');
    if (closeButton) { close(); return; }
    const trigger = event.target.closest('.bg-cart-link, [data-action="open-drawer"][data-drawer-id="sidebar-cart"]');
    if (trigger && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0 && dialog.dataset.bgcEnabled === 'true') {
      event.preventDefault(); event.stopImmediatePropagation(); open(trigger); return;
    }
    const action = event.target.closest('[data-bgc-step], [data-bgc-remove]');
    if (!action) return;
    event.preventDefault();
    if (busy) return;
    const line = action.closest('[data-bgc-key]');
    const input = line.querySelector('[data-bgc-quantity]');
    let quantity = 0;
    if (action.hasAttribute('data-bgc-step')) {
      quantity = Number(input.value) + Number(action.dataset.bgcStep) * Number(input.step || 1);
      const min = Number(input.dataset.min || 1);
      if (quantity < min) quantity = Number(action.dataset.bgcStep) < 0 ? 0 : min;
      input.value = Math.max(0, quantity);
      if (!input.reportValidity()) return;
    }
    perform(dialog.dataset.changeUrl, {id: line.dataset.bgcKey, quantity});
  }, true);
  document.addEventListener('input', event => {
    if (event.target.matches('[data-bgc-form] [name="note"]')) {
      document.querySelectorAll('[data-bgc-form] [name="note"]').forEach(note => { note.value = event.target.value; note.dataset.bgcDirty = 'true'; });
    }
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-bgc-form]')) return;
    if (busy) { event.preventDefault(); return; }
    if (event.submitter?.name === 'checkout') return; // Native POST /cart with checkout and current quantities/note.
    event.preventDefault();
    const updates = Object.fromEntries([...form.querySelectorAll('[data-bgc-quantity]')].map(input => [input.dataset.key, Number(input.value)]));
    const payload = {updates};
    const note = form.querySelector('[name="note"]');
    if (note) { payload.note = note.value; document.querySelectorAll('[data-bgc-form] [name="note"]').forEach(node => { delete node.dataset.bgcDirty; }); }
    perform(dialog.dataset.updateUrl, payload);
  });
  document.addEventListener('cart:refresh', () => perform());
  document.addEventListener('product:added', () => dialog.dataset.bgcEnabled === 'true' ? open() : perform());
  document.addEventListener('shopify:section:load', enhance);
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
  enhance();
})();
